import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Info,
  BarChart3,
  FileSearch,
  RefreshCw,
  Download,
  TrendingUp,
  Globe
} from 'lucide-react';
import {
  auditCityLanding,
  batchAuditCities,
  calculateAggregateStats,
  SEOAuditResult,
  AggregateStats
} from '@/utils/seo/seoAuditor';
import { topCitiesData } from '@/data/countries';
import { City } from '@/data/cities';

const SEOValidationDashboard: React.FC = () => {
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditProgress, setAuditProgress] = useState(0);
  const [auditResults, setAuditResults] = useState<SEOAuditResult[]>([]);
  const [aggregateStats, setAggregateStats] = useState<AggregateStats | null>(null);
  const [selectedCity, setSelectedCity] = useState<SEOAuditResult | null>(null);

  // Get total cities count
  const totalCitiesAvailable = topCitiesData.length;

  const startAudit = async (sampleSize: number = 100) => {
    setIsAuditing(true);
    setAuditProgress(0);
    setAuditResults([]);

    // Get sample cities from topCitiesData and convert to City format
    const sampleCities: City[] = topCitiesData
      .slice(0, Math.min(sampleSize, topCitiesData.length))
      .map((cityData: any): City => {
        // Extract country code from country name (simple approach)
        const countryCode = (cityData.country || 'Unknown')
          .substring(0, 2)
          .toUpperCase();
        
        return {
          id: cityData.slug,
          name: cityData.name,
          country: cityData.country || 'Unknown',
          countryCode,
          population: cityData.population || 50000,
          coordinates: { lat: 0, lng: 0 },
          language: 'en',
          timezone: 'UTC',
          slug: cityData.slug
        };
      });

    // Run batch audit
    const results = await batchAuditCities(
      sampleCities,
      'en',
      (current, total) => {
        setAuditProgress((current / total) * 100);
      }
    );

    setAuditResults(results);
    setAggregateStats(calculateAggregateStats(results));
    setIsAuditing(false);
  };

  const exportResults = () => {
    const dataStr = JSON.stringify(auditResults, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seo-audit-${new Date().toISOString()}.json`;
    link.click();
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
          <BarChart3 className="h-8 w-8" />
          SEO Validation Dashboard
        </h1>
        <p className="text-muted-foreground">
          Automated SEO compliance monitoring for {totalCitiesAvailable.toLocaleString()} city landing pages
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Landings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalCitiesAvailable.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Available for audit
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Audited Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{auditResults.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {auditResults.length > 0 
                ? `${((auditResults.length / totalCitiesAvailable) * 100).toFixed(2)}% coverage`
                : 'No audit performed yet'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Average SEO Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {aggregateStats ? aggregateStats.averageScore.toFixed(1) : '—'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Out of 100
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pages with Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {aggregateStats ? aggregateStats.citiesWithIssues : '—'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {aggregateStats && auditResults.length > 0
                ? `${((aggregateStats.citiesWithIssues / auditResults.length) * 100).toFixed(1)}% need attention`
                : 'Run audit to see details'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Control Panel */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Audit Controls</CardTitle>
          <CardDescription>
            Run SEO audits on a sample of city landing pages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => startAudit(100)}
              disabled={isAuditing}
              className="gap-2"
            >
              {isAuditing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Auditing...
                </>
              ) : (
                <>
                  <FileSearch className="h-4 w-4" />
                  Audit 100 Cities
                </>
              )}
            </Button>

            <Button
              onClick={() => startAudit(500)}
              disabled={isAuditing}
              variant="outline"
              className="gap-2"
            >
              <FileSearch className="h-4 w-4" />
              Audit 500 Cities
            </Button>

            {auditResults.length > 0 && (
              <Button
                onClick={exportResults}
                variant="outline"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export Results
              </Button>
            )}
          </div>

          {isAuditing && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Audit Progress</span>
                <span>{auditProgress.toFixed(0)}%</span>
              </div>
              <Progress value={auditProgress} />
            </div>
          )}
        </CardContent>
      </Card>

      {aggregateStats && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="distribution">Score Distribution</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="details">City Details</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Content Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Average Word Count</span>
                    <span className="font-semibold">
                      {aggregateStats.averageWordCount.toFixed(0)} words
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Keyword Density</span>
                    <span className="font-semibold">
                      {aggregateStats.averageKeywordDensity.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Issues</span>
                    <span className="font-semibold">
                      {aggregateStats.criticalIssuesCount + aggregateStats.warningIssuesCount}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Issue Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      Critical Issues
                    </span>
                    <Badge variant="destructive">
                      {aggregateStats.criticalIssuesCount}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      Warnings
                    </span>
                    <Badge variant="secondary">
                      {aggregateStats.warningIssuesCount}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Pages Without Issues
                    </span>
                    <Badge variant="outline">
                      {auditResults.length - aggregateStats.citiesWithIssues}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="distribution" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>SEO Score Distribution</CardTitle>
                <CardDescription>
                  Performance breakdown across audited pages
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Excellent (90-100)</span>
                      <span className="text-sm text-muted-foreground">
                        {aggregateStats.scoreDistribution.excellent} pages
                      </span>
                    </div>
                    <Progress
                      value={(aggregateStats.scoreDistribution.excellent / auditResults.length) * 100}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Good (75-89)</span>
                      <span className="text-sm text-muted-foreground">
                        {aggregateStats.scoreDistribution.good} pages
                      </span>
                    </div>
                    <Progress
                      value={(aggregateStats.scoreDistribution.good / auditResults.length) * 100}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Fair (60-74)</span>
                      <span className="text-sm text-muted-foreground">
                        {aggregateStats.scoreDistribution.fair} pages
                      </span>
                    </div>
                    <Progress
                      value={(aggregateStats.scoreDistribution.fair / auditResults.length) * 100}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Poor (0-59)</span>
                      <span className="text-sm text-muted-foreground">
                        {aggregateStats.scoreDistribution.poor} pages
                      </span>
                    </div>
                    <Progress
                      value={(aggregateStats.scoreDistribution.poor / auditResults.length) * 100}
                      className="h-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="issues" className="space-y-4">
            {auditResults
              .filter(result => result.issues.length > 0)
              .slice(0, 10)
              .map(result => (
                <Card key={result.cityId}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{result.cityName}</CardTitle>
                        <CardDescription>
                          {result.issues.length} issue{result.issues.length !== 1 ? 's' : ''} found
                        </CardDescription>
                      </div>
                      <Badge
                        variant={
                          result.score >= 90
                            ? 'default'
                            : result.score >= 75
                            ? 'secondary'
                            : 'destructive'
                        }
                      >
                        Score: {result.score}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {result.issues.map((issue, idx) => (
                      <Alert
                        key={idx}
                        variant={issue.severity === 'critical' ? 'destructive' : 'default'}
                      >
                        {issue.severity === 'critical' && (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        {issue.severity === 'warning' && (
                          <AlertTriangle className="h-4 w-4" />
                        )}
                        {issue.severity === 'info' && <Info className="h-4 w-4" />}
                        <AlertTitle>{issue.category}</AlertTitle>
                        <AlertDescription>
                          {issue.message}
                          {issue.recommendation && (
                            <div className="mt-1 text-xs">
                              <strong>Recommendation:</strong> {issue.recommendation}
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </CardContent>
                </Card>
              ))}
          </TabsContent>

          <TabsContent value="details" className="space-y-4">
            <div className="grid gap-4">
              {auditResults.slice(0, 20).map(result => (
                <Card key={result.cityId} className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => setSelectedCity(result)}
                >
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-semibold">{result.cityName}</div>
                          <div className="text-sm text-muted-foreground">
                            {result.metrics.wordCount} words • {result.metrics.h2Count} H2 sections
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{result.score}</div>
                        <div className="text-xs text-muted-foreground">
                          {result.issues.length} issues
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {!aggregateStats && !isAuditing && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileSearch className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Audit Data</h3>
            <p className="text-muted-foreground mb-4">
              Run an audit to see SEO performance metrics for your city landing pages
            </p>
            <Button onClick={() => startAudit(100)}>
              Start First Audit
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SEOValidationDashboard;
